// agent-task-meta — the single source for agent/task display + route metadata.
//
// AGENT_NAMES, TASK_TYPE_LABELS, TASK_TYPE_ROUTES and routeForTask were
// previously declared verbatim in BOTH AgentTaskCard.tsx and
// use-review-queue.ts. Two copies of an exhaustive Record<AgentTaskType, …>
// drift independently whenever a new agent/task type is added, so they live
// here and are imported by both. AgentTaskCard re-exports them for its own
// downstream consumers (RunDetailPanel, AgentKanbanBoard). Keeping the maps in
// a dependency-free leaf module also avoids the AgentTaskCard ↔ use-review-queue
// import cycle (AgentTaskCard imports useArtefactSummary from use-review-queue).
import type { AgentTask, AgentTaskType } from '@septima/contract';

export const AGENT_NAMES: Record<AgentTaskType, string> = {
    post_call_processing: 'ATLAS',
    call_classification_batch: 'ATLAS',
    post_call_processing_batch: 'ATLAS',
    fact_find_prefill_batch: 'ATLAS',
    fact_find_document_extraction: 'ATLAS',
    suitability_document_extraction: 'ATLAS',
    monthly_report: 'ATLAS',
    monthly_narrative: 'ATLAS',
    suitability_check: 'VIGIL',
    kyc_renewal: 'AEGIS',
    consumer_duty_rescore: 'AEGIS',
    breach_draft: 'AEGIS',
    sanctions_screening: 'AEGIS',
    reconciliation_classification_batch: 'MASON',
    coac_pdf_extraction: 'CEDAR',
    coac_breakdown: 'CEDAR',
    suitability_letter_draft: 'SCRIVENER',
    fact_find_change_summary: 'SCRIVENER',
    onboarding_pack_draft: 'HERALD',
    email_draft: 'EMISSARY',
    onboarding_pipeline_watch: 'WARDEN',
    vulnerability_flag: 'SENTINEL',
    framework_deviation: 'QUORUM',
    fund_policy_watch: 'BEACON',
    data_freshness_check: 'PULSE',
    fund_rns_draft: 'CHARTER',
    vulnerability_adaptation_plan: 'HAVEN',
    governance_paper_draft: 'FORUM',
    fair_value_assessment: 'ARBITER',
    workflow_run: 'WORKFLOW',
};

export const TASK_TYPE_LABELS: Record<AgentTaskType, string> = {
    post_call_processing: 'Post-call processing',
    call_classification_batch: 'Call classification (batch)',
    post_call_processing_batch: 'Post-call processing (batch)',
    fact_find_prefill_batch: 'Fact-find prefill (batch)',
    fact_find_document_extraction: 'Fact-find document extraction',
    suitability_document_extraction: 'Suitability document extraction',
    suitability_check: 'Suitability check',
    monthly_report: 'Monthly report',
    monthly_narrative: 'Monthly narrative',
    kyc_renewal: 'KYC renewal',
    consumer_duty_rescore: 'Consumer Duty rescore',
    breach_draft: 'Breach draft',
    sanctions_screening: 'Sanctions screening',
    reconciliation_classification_batch: 'Reconciliation classification',
    coac_pdf_extraction: 'CA PDF extraction',
    coac_breakdown: 'CA breakdown',
    suitability_letter_draft: 'Suitability letter',
    fact_find_change_summary: 'Fact find change summary',
    onboarding_pack_draft: 'Onboarding pack',
    email_draft: 'Email draft',
    onboarding_pipeline_watch: 'Onboarding pipeline watch',
    vulnerability_flag: 'Vulnerability flag',
    framework_deviation: 'Framework deviation',
    fund_policy_watch: 'Fund policy watch',
    data_freshness_check: 'Data freshness check',
    fund_rns_draft: 'Fund RNS draft',
    vulnerability_adaptation_plan: 'Vulnerability adaptation plan',
    governance_paper_draft: 'Governance paper draft',
    fair_value_assessment: 'Fair value assessment',
    workflow_run: 'Workflow run',
};

export const TASK_TYPE_ROUTES: Record<AgentTaskType, string> = {
    post_call_processing: '/desk/calls-meetings',
    // Classification gates the downstream passes; its row lives on the calls
    // surface where the callKind badge it sets is shown.
    call_classification_batch: '/desk/calls-meetings',
    // Batched knowledge fan-out lands on the same Client Knowledge surface as
    // the artefacts it produces; prefill batches land on the fact-find register.
    post_call_processing_batch: '/desk/client-knowledge',
    fact_find_prefill_batch: '/desk/fact-find',
    fact_find_document_extraction: '/desk/fact-find',
    suitability_document_extraction: '/desk/suitability',
    suitability_check: '/comply/suitability',
    monthly_report: '/ops/reporting',
    // MonthlyNarrativePanel renders on /desk/client-knowledge — the
    // routeForTask helper threads ?client=<id> from the task payload.
    monthly_narrative: '/desk/client-knowledge',
    kyc_renewal: '/comply/fin-crime-kyc',
    breach_draft: '/comply/breach-register',
    sanctions_screening: '/comply/fin-crime-kyc',
    consumer_duty_rescore: '/comply/consumer-duty',
    reconciliation_classification_batch: '/ops/reconciliation',
    coac_pdf_extraction: '/ops/corporate-actions',
    // The IM-facing CEDAR explainer lives on the Desk My-decisions inbox.
    coac_breakdown: '/desk/corporate-actions',
    // A suitability letter is a Desk surface. The Desk Suitability page is open.
    suitability_letter_draft: '/desk/suitability',
    // The summary sits at the top of the refresh fact find (SEP-351).
    fact_find_change_summary: '/desk/fact-find',
    onboarding_pack_draft: '/desk/my-book',
    email_draft: '/desk/calls-meetings',
    onboarding_pipeline_watch: '/ops/onboarding',
    vulnerability_flag: '/comply/vulnerability',
    framework_deviation: '/desk/framework-compliance',
    fund_policy_watch: '/view/fund-management',
    data_freshness_check: '/comply/data-freshness',
    fund_rns_draft: '/view/fund-management',
    vulnerability_adaptation_plan: '/comply/vulnerability',
    // ManComm Programme view is unwired (issue #208) — land governance paper
    // reviews on the View home instead of deep-linking into the hidden page.
    governance_paper_draft: '/view/dashboard',
    fair_value_assessment: '/comply/fair-value',
    // WS-A — workflow runs open the editor. Ops is the default. `routeForTask`
    // sends the run to the frontend of the task, because each view has the page.
    workflow_run: '/ops/workflows',
};

// Compute the per-task review URL — most task types share the static
// route from TASK_TYPE_ROUTES, but post_call_processing also threads
// the call id from the task payload so the calls-meetings page can
// auto-expand the right row instead of dumping the IM at the list view.
export function routeForTask(task: AgentTask): string {
    if (task.type === 'onboarding_pipeline_watch') {
        if (task.frontend === 'desk') return '/desk/onboarding';
        if (task.frontend === 'comply') return '/comply/onboarding';
        if (task.frontend === 'view') return '/view/onboarding';
        return '/ops/onboarding';
    }
    // Each of the four views has a Workflows page. A run opens in the view of
    // the task, so its owner is not sent to a page that the owner cannot open.
    if (
        task.type === 'workflow_run' &&
        (task.frontend === 'desk' ||
            task.frontend === 'ops' ||
            task.frontend === 'comply' ||
            task.frontend === 'view')
    ) {
        return `/${task.frontend}/workflows`;
    }
    const base = TASK_TYPE_ROUTES[task.type];
    if (task.type === 'post_call_processing') {
        const callId = task.payload?.callId;
        if (typeof callId === 'string' && callId.length > 0) {
            return `${base}?call=${encodeURIComponent(callId)}`;
        }
    }
    if (task.type === 'monthly_narrative') {
        const clientId = task.payload?.clientId ?? task.clientId;
        if (typeof clientId === 'string' && clientId.length > 0) {
            return `${base}?client=${encodeURIComponent(clientId)}`;
        }
    }
    return base;
}
